/**
 * "Per day" in the Arena means the day in Lisbon, whatever the server's clock
 * says. Everything is stored in UTC; these helpers find where a Lisbon day
 * starts and ends in UTC, including the days the clocks change.
 */
export const ARENA_TIME_ZONE = 'Europe/Lisbon';

const dateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ARENA_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ARENA_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** `YYYY-MM-DD` of the Lisbon day that contains `instant`. */
export function lisbonDate(instant: Date): string {
  return dateFormatter.format(instant);
}

/** Offset of Lisbon from UTC at `instant`, in milliseconds (0 or 3 600 000). */
function lisbonOffsetMs(instant: Date): number {
  const parts = Object.fromEntries(
    partsFormatter.formatToParts(instant).map((part) => [part.type, part.value]),
  );
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** UTC instant of 00:00 in Lisbon on `date` (`YYYY-MM-DD`). */
export function lisbonMidnight(date: string): Date {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const naive = Date.UTC(year, month - 1, day);
  // Midnight is never inside a DST gap in Lisbon (changes happen at 01:00 and
  // 02:00), so one correction with the offset at the guess is enough.
  const guess = new Date(naive - lisbonOffsetMs(new Date(naive)));
  return new Date(naive - lisbonOffsetMs(guess));
}

/** `[start, end)` in UTC of the Lisbon day that contains `instant`. */
export function lisbonDayBounds(instant: Date): { start: Date; end: Date } {
  const date = lisbonDate(instant);
  const start = lisbonMidnight(date);
  // Noon of the next day is always on the next date, even on 23 h and 25 h days.
  const next = lisbonDate(new Date(start.getTime() + 36 * 3_600_000));
  return { start, end: lisbonMidnight(next) };
}

/** Lisbon date and hour (0-23) of `instant`. */
export function lisbonHour(instant: Date): { date: string; hour: number } {
  const parts = Object.fromEntries(
    partsFormatter.formatToParts(instant).map((part) => [part.type, part.value]),
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, hour: Number(parts.hour) };
}
